import ICDelete from '@assets/jsx-icon/ICDelete';
import BaseTableRender from '@components/BaseTableRender';
import StyledDateRangePicker, { DateRangeValue } from '@components/date-range-select/StyledDateRangePicker';
import FilterRoot from '@components/filter/FilterRoot';
import PopUpWarning from '@components/PopUpWarning';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { BaseHead, CellOfRow } from '@components/tables';
import { contraventionProcessingJobHttpController } from '@controllers/index';
import { defaultPagination } from '@Core';
import { ContraventionProcessingJobStatus, ContraventionProcessingJobType } from '@EcamModel/model';
import usePopUp from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import color from '@theme/Colors';
import moment from 'moment';
import { useState } from 'react';
import { IoEyeOutline } from 'react-icons/io5';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateTime } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import InformationIcon from '../icon/InformationIcon';
import {
    BackdatingMode,
    backdateStatusMapping,
    capitalizeFirst,
    statusBackdateOptions,
    statusContraventionReviewSessionOptions,
} from './utils';

interface ReviewSessionListScreenProps {
    type: ContraventionProcessingJobType;
}

export default function ReviewSessionListScreen({ type }: ReviewSessionListScreenProps) {
    const { id } = useParams<{ id: string }>();
    const cameraId = Number(id || '0');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const backdrop = useBackdrop();

    const popUpWarningDelete = usePopUp();

    const [selectedStatus, setSelectedStatus] = useState<ContraventionProcessingJobStatus | undefined>();
    const [selectedBackdateMode, setSelectedBackdateMode] = useState<BackdatingMode | undefined>();
    const [dateRange, setDateRange] = useState<DateRangeValue>({});
    const [page, setPage] = useState(1);
    const [deleteId, setDeleteId] = useState<number | null>(null);

    const isForecast = type === ContraventionProcessingJobType.Forecast;
    const title = isForecast ? 'potential PCN' : 'backdate PCN';
    const path = isForecast ? 'potential-pcn' : 'backdate';

    const getContraventionReviewSession = async () => {
        return await contraventionProcessingJobHttpController.list({
            filter: {
                CameraId: cameraId,
                Type: type,
                Status: selectedStatus ?? undefined,
                IsBackDateEnQueue:
                    !isForecast && selectedBackdateMode !== undefined
                        ? selectedBackdateMode === BackdatingMode.AUTO_ENQUEUE
                        : undefined,
            },
            From: dateRange.startDate,
            To: dateRange.endDate,
            sorts: ['-Created'],
            page: page,
        });
    };

    const { data: contraventionReviewSessions = defaultPagination(), isLoading } = useQuery(
        [`${path}-runs`, cameraId, page, dateRange.endDate, selectedStatus, selectedBackdateMode],
        () => getContraventionReviewSession(),
        {
            enabled: !!cameraId,
            refetchInterval: (data) => {
                const inProgress = data?.rows?.some(
                    (item: any) => item.Status === ContraventionProcessingJobStatus.InProgress
                );

                return inProgress ? 10000 : false;
            },
        }
    );

    const statusAutocomplete = useStyledAutocomplete<any, any>({
        list: {
            options: statusContraventionReviewSessionOptions,
            isFiltered: (option, searchText) => option.Name.toLowerCase().includes(searchText.toLowerCase()),
        },
    });

    const backdateModeAutocomplete = useStyledAutocomplete<any, any>({
        list: {
            options: statusBackdateOptions,
            isFiltered: (option, searchText) => option.Name.toLowerCase().includes(searchText.toLowerCase()),
        },
    });

    const currentBackdateModeValue = statusBackdateOptions.find((opt) => opt.Id === selectedBackdateMode);
    const currentStatusValue = statusContraventionReviewSessionOptions.find((opt) => opt.Id === selectedStatus);

    const handleRunClick = (runId: number) => {
        navigate(`/cameras/${cameraId}/${path}-runs/${runId}`);
    };

    const handleNewClick = () => {
        navigate(`/cameras/${cameraId}/new-${path}`);
    };

    const handleResetFilter = () => {
        setSelectedStatus(undefined);
        setSelectedBackdateMode(undefined);
        setDateRange({});
    };

    const handleDeleteBackdate = async (id: number) => {
        await contraventionProcessingJobHttpController.deleteContraventionProcessingJob({
            ContraventionProcessingJobId: id,
        });
    };

    const handleOpenDelete = (id: number) => {
        setDeleteId(id);
        popUpWarningDelete.setTrue();
    };

    const handleCloseDelete = () => {
        popUpWarningDelete.onClose();
        setDeleteId(null);
    };

    const handleConfirmDelete = async () => {
        if (!deleteId) return;
        backdrop.setTrue();
        await handleDeleteBackdate(deleteId).finally(() => {
            backdrop.setFalse();
        });
        handleCloseDelete();
        queryClient.invalidateQueries([`${path}-runs`, cameraId]);
    };

    const formatUtcDate = (inp: Date) => {
        return moment.utc(inp).format('DD/MM/YYYY');
    };

    if (!cameraId) {
        return (
            <Stack justifyContent="center" alignItems="center" py={8}>
                <Typography color="error">Invalid camera ID</Typography>
            </Stack>
        );
    }

    if (isLoading) {
        return (
            <Stack justifyContent="center" alignItems="center" py={8}>
                <CircularProgress size={40} />
            </Stack>
        );
    }

    return (
        <Stack
            direction={'column'}
            spacing={2}
            sx={{
                padding: 2,
                borderRadius: '0px 16px 16px 16px',
                border: '1px solid #E4E7EC',
            }}
        >
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography variant="h4">{`${capitalizeFirst(title)} list`}</Typography>
                <LoadingButton
                    variant="contained"
                    loadingPosition="center"
                    onClick={handleNewClick}
                    sx={{
                        flex: '0 0 auto',
                        minWidth: 140,
                    }}
                >
                    {`New ${title}`}
                </LoadingButton>
            </Stack>

            <FilterRoot
                handleResetFilter={handleResetFilter}
                filterChild={
                    <>
                        <Stack spacing={2} direction={'row'} flex={1} mt={1}>
                            <Stack flex={1}>
                                <StyledDateRangePicker
                                    value={dateRange}
                                    onChange={setDateRange}
                                    label="Select run date"
                                    placeholder="Select run date range"
                                />
                            </Stack>

                            <Stack flex={1}>
                                <StyledAutocomplete
                                    {...statusAutocomplete}
                                    getOptionLabel={(option) => option?.Name || ''}
                                    value={currentStatusValue}
                                    onChange={(option) => {
                                        setSelectedStatus(option?.Id);
                                    }}
                                    label="Status"
                                    placeholder="All Statuses"
                                />
                            </Stack>

                            {!isForecast && (
                                <Stack flex={1}>
                                    <StyledAutocomplete
                                        {...backdateModeAutocomplete}
                                        getOptionLabel={(option) => option?.Name || ''}
                                        value={currentBackdateModeValue}
                                        onChange={(option) => {
                                            setSelectedBackdateMode(option?.Id);
                                        }}
                                        label="Mode"
                                        placeholder="All Modes"
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </>
                }
            />

            <Stack mt={3} mb={5}>
                <BaseTableRender
                    paging={contraventionReviewSessions}
                    setPage={(page) => setPage(page)}
                    isLoading={false}
                    tableComponent={
                        <TableContainer component={Box} sx={{}}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <BaseHead align="left" title="Name" />
                                        <BaseHead align="center" title={'Performed by'} />
                                        <BaseHead align="center" title={'Run Date'} />
                                        <BaseHead align="center" title={'Status'} />
                                        <BaseHead align="center" title={'Total PCN'} />
                                        {!isForecast && (
                                            <>
                                                <BaseHead align="center" title={'In progress'} />
                                                <BaseHead align="center" title={'Approved'} />
                                                <BaseHead align="center" title={'Cancelled'} />
                                            </>
                                        )}
                                        <BaseHead align="center" title={''} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contraventionReviewSessions?.rows?.map((item, index) => {
                                        const isRowInProgress =
                                            item.Status === ContraventionProcessingJobStatus.InProgress;
                                        return (
                                            <TableRow key={item.Id!}>
                                                <CellOfRow
                                                    value={
                                                        <Typography
                                                            component={'span'}
                                                        >{`${capitalizeFirst(title)} (${formatUtcDate(item.FromDate)} - ${formatUtcDate(item.ToDate)})`}</Typography>
                                                    }
                                                    align="left"
                                                    sx={{ width: '20%' }}
                                                />
                                                <CellOfRow
                                                    value={item?.CreatedBy?.replace('@ukparkingcontrol.com', '') ?? ''}
                                                    sx={{ width: isForecast ? '20%' : '10%' }}
                                                    align="center"
                                                />
                                                <CellOfRow
                                                    value={formatDateTime(item.Created!)}
                                                    align="center"
                                                    sx={{ width: isForecast ? '20%' : '15%' }}
                                                />
                                                <CellOfRow
                                                    value={
                                                        <Stack
                                                            direction={'row'}
                                                            spacing={1}
                                                            alignItems={'center'}
                                                            justifyContent={'center'}
                                                        >
                                                            <Typography>
                                                                {backdateStatusMapping[item?.Status ?? -1]}
                                                            </Typography>
                                                            {isRowInProgress && (
                                                                <Tooltip
                                                                    placement="top"
                                                                    title="Please wait while the system processes the data."
                                                                >
                                                                    <InformationIcon />
                                                                </Tooltip>
                                                            )}
                                                        </Stack>
                                                    }
                                                    align="center"
                                                    sx={{ width: isForecast ? '20%' : '10%' }}
                                                />
                                                <CellOfRow
                                                    value={item?.PotentialContraventions ?? 0}
                                                    align="center"
                                                    sx={{ width: isForecast ? '20%' : '10%' }}
                                                />
                                                {!isForecast && (
                                                    <>
                                                        <CellOfRow
                                                            value={item?.InProgressCount ?? 0}
                                                            align="center"
                                                            sx={{ width: '10%' }}
                                                        />
                                                        <CellOfRow
                                                            value={item?.ApprovedCount ?? 0}
                                                            align="center"
                                                            sx={{ width: '10%' }}
                                                        />
                                                        <CellOfRow
                                                            value={item?.CancelledCount ?? 0}
                                                            align="center"
                                                            sx={{ width: '15%' }}
                                                        />
                                                    </>
                                                )}
                                                <CellOfRow
                                                    value={
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Tooltip title={'View'} placement="top">
                                                                <IconButton
                                                                    sx={{
                                                                        backgroundColor: color.grey200,
                                                                        border: `6px solid ${color.grey100}`,
                                                                        width: '40px',
                                                                        height: '40px',
                                                                        position: 'sticky',
                                                                    }}
                                                                    onClick={() => handleRunClick(item.Id!)}
                                                                >
                                                                    <IoEyeOutline />
                                                                </IconButton>
                                                            </Tooltip>

                                                            {(isForecast ||
                                                                (item?.InProgressCount === 0 &&
                                                                    item?.ApprovedCount === 0 &&
                                                                    item?.CancelledCount === 0)) && (
                                                                <Tooltip title={'Delete'} placement="top">
                                                                    <IconButton
                                                                        sx={{
                                                                            backgroundColor: color.grey200,
                                                                            border: `6px solid ${color.grey100}`,
                                                                            width: '40px',
                                                                            height: '40px',
                                                                            position: 'sticky',
                                                                        }}
                                                                        size="small"
                                                                        onClick={() => handleOpenDelete(item.Id!)}
                                                                    >
                                                                        <ICDelete />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    }
                                                    align="center"
                                                    sx={{ width: '15%' }}
                                                />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                />
            </Stack>

            <PopUpWarning
                open={popUpWarningDelete.open}
                onClose={handleCloseDelete}
                onConfirm={handleConfirmDelete}
                title="Warning"
                message={`Are you sure you want to delete this ${title}?`}
            />
        </Stack>
    );
}
